import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Sprout, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { trackEvent } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDED = [
    '1 organisation · 1 projet actif · jusqu\'à 3 utilisateurs',
    'GANTT, Budget, GED, Valley AI, Visioconférence',
    'Campus 360° : formation certifiante incluse',
    'Exports PDF · données hébergées en Europe (RGPD)',
];

export default function SignupFree() {
    const navigate = useNavigate();
    useEffect(() => { trackEvent('signup_view'); }, []);
    const [form, setForm] = useState({ full_name: '', email: '', password: '', website: '' });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 10) {
            toast.error('Le mot de passe doit contenir au moins 10 caractères.');
            return;
        }
        if (form.website) return;
        if (!acceptTerms) {
            toast.error("Veuillez accepter les Conditions générales d'utilisation.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/auth/signup`, {
                email: form.email.trim(), password: form.password, full_name: form.full_name.trim(),
                source: 'signup-free',
            });
            const { error } = await supabase.auth.signInWithPassword({
                email: form.email.trim(), password: form.password,
            });
            if (error) throw new Error(error.message);
            toast.success('Bienvenue ! Créons votre espace de travail.');
            navigate('/app/onboarding');
        } catch (err) {
            const detail = err.response?.data?.detail || err.message;
            toast.error(typeof detail === 'string' ? detail : 'Inscription impossible, réessayez.');
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="page-signup-free">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="signup-free-logo">
                        <img src="/brand/african-valley-logo-96.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover" />
                        <span className="font-heading text-2xl"><span className="text-foreground">African</span> <span className="text-primary">Valley</span></span>
                    </Link>
                    <Link to="/auth/signin" className="text-sm text-foreground/70 hover:text-foreground">Déjà un compte ? Connexion →</Link>
                </div>
            </header>

            <main className="container-page py-14 lg:py-20">
                <div className="grid lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
                    <div>
                        <div className="eyebrow mb-3">Offre Explorer — gratuite en permanence</div>
                        <h1 className="font-heading text-4xl sm:text-5xl mb-5" data-testid="signup-free-title">
                            🌱 Créez votre espace <span className="text-primary">gratuit</span>, tout de suite.
                        </h1>
                        <p className="text-foreground/70 leading-relaxed mb-6">
                            Aucune carte bancaire. <strong>Gratuit en permanence — sans limite de durée.</strong> Passez
                            à une offre supérieure uniquement quand vos besoins grandissent.
                        </p>
                        <ul className="space-y-2.5 text-sm text-foreground/80">
                            {INCLUDED.map(f => (
                                <li key={f} className="flex items-start gap-2">
                                    <Check size={15} className="text-secondary mt-0.5 shrink-0" /> {f}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="card-soft p-7 lg:p-8" data-testid="signup-free-card">
                        <div className="flex items-center gap-2 mb-1">
                            <Sprout size={18} className="text-primary" />
                            <h2 className="font-heading text-xl">Inscription immédiate</h2>
                        </div>
                        <p className="text-sm text-muted-foreground mb-5">Votre espace est prêt en moins d'une minute.</p>
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label htmlFor="sf-name">Nom & prénom *</Label>
                                <Input id="sf-name" value={form.full_name} onChange={set('full_name')} required minLength={2} data-testid="signup-free-name" />
                            </div>
                            <div>
                                <Label htmlFor="sf-email">Email professionnel *</Label>
                                <Input id="sf-email" type="email" value={form.email} onChange={set('email')} required data-testid="signup-free-email" />
                            </div>
                            <div>
                                <Label htmlFor="sf-pwd">Mot de passe * <span className="text-xs text-muted-foreground">(10 caractères min.)</span></Label>
                                <Input id="sf-pwd" type="password" value={form.password} onChange={set('password')} required minLength={10} data-testid="signup-free-password" />
                            </div>
                            <input type="text" value={form.website} onChange={set('website')} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                            <label className="flex items-start gap-2.5 text-sm text-foreground/80 cursor-pointer select-none">
                                <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                                    required className="mt-0.5 h-4 w-4 accent-primary" data-testid="signup-free-terms-checkbox" />
                                <span>
                                    J'accepte les <Link to="/p/terms" target="_blank" className="underline text-primary" data-testid="signup-free-terms-link">Conditions générales d'utilisation</Link> et
                                    la <Link to="/p/privacy" target="_blank" className="underline">politique de confidentialité</Link>. *
                                </span>
                            </label>
                            <Button type="submit" disabled={busy || !acceptTerms} className="w-full bg-primary hover:bg-primary-hover" data-testid="signup-free-submit">
                                {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <ArrowRight size={15} className="mr-2" />}
                                Créer mon espace gratuit
                            </Button>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                                Besoin d'un accompagnement personnalisé ? <Link to="/apply" className="text-primary underline">Demandez un accès accompagné</Link>.
                            </p>
                        </form>
                    </div>
                </div>
            </main>
        </div>
    );
}
